<template>
  <expansion-card :icon="icon">
    <!-- label -->
    <span slot="title">{{ (label || '') + (required ? ' *' : '') }}</span>

    <template slot="title-right">
      <slot name="title-right" />
    </template>
    <slot name="heading" />

    <v-data-table
      ref="editorListing"
      :mobile-breakpoint="0"
      :headers="columnHeaders"
      :items="value"
      hide-default-footer
      :custom-filter="customFilter"
      :search="search"
      :items-per-page="-1"
      class="ft-detail-editor-table"
      :no-data-text="'No ' + label + ' Found'"
    >
      <template v-slot:item="props">
        <tr
          v-if="!hideRows[props.index]"
          :key="itemKey(props.item)"
          :class="getRowClass(props.item, props.index)"
          @click="rowSelected(props.item)"
        >
          <td
            v-if="sortable"
            class="text-xs-left pr-0 ft-thin-cell"
          >
            <v-btn
              style="cursor: move"
              :disabled="disabled"
              icon
              class="sortableHandle"
            >
              <v-icon color="grey darken-1">
                mdi-drag-horizontal
              </v-icon>
            </v-btn>
          </td>
          <td
            v-for="(header, i) of headers.filter((h, i) => i > 0 || !sortable)"
            :key="i"
            :class="[getCellClass(header, props.item, i), {'ft-thin-cell': header.thin, 'ft-small-cell': header.small}]"
          >
            <!-- If has icon -->
            <v-icon
              v-if="getCellIcon(header, props.item, i)"
              :class="getCellIconClass(header, props.item, i)"
            >
              {{ getCellIcon(header, props.item, i) }}
            </v-icon>
            <!-- If is input field -->
            <formatted-input
              v-if="inputText(header, props.item, i)"
              v-show="!header.editor.hideIf || !header.editor.hideIf(props.item, props, header)"
              :ref="buildRefName(header.value, props.index)"
              :size="header.editor.size"
              :style="header.editor.style"
              :type="numberInputs.includes(header.editor.type) ? 'number' : ''"
              :class="[header.editor.class, {'ft-small-input': header.editor.type === 'int'}]"
              :blurred-format="blurredFormat(header.editor)"
              :prefix="header.editor.prefix"
              :required="header.editor.required"
              :rules="header.editor.rules"
              :placeholder="header.editor.placeholder"
              :value="props.item[header.value]"
              :error-messages="getPathErrors(jsonPath, props.index, header.value)"
              :disabled="disabled"
              @blur="setFilteredResult(_self, props.item, header.value, header.editor)"
              @input="setCellValue(props.item, header.value, header.editor, props.index, $event)"
            />
            <!-- If is input switch -->
            <v-switch
              v-else-if="inputSwitch(header, props.item, i)"
              v-show="!header.editor.hideIf || !header.editor.hideIf(props.item, props, header)"
              :color="$ftStyle.switch"
              :input-value="getCellValue(header, props.item, i)"
              :error-messages="getPathErrors(jsonPath, props.index, header.value)"
              :disabled="disabled"
              @change="setCellValue(props.item, header.value, header.editor, props.index, $event)"
            />
            <!-- If is empty - for use with named cell slot -->
            <!-- named cell slot -->
            <slot
              v-else-if="inputSlot(header, props.item, i)"
              v-show="!header.editor.hideIf || !header.editor.hideIf(props.item, props, header)"
              :name="header.value"
              :row="props.index"
              :column="i"
              :item="props.item"
              :header="header"
              :items="value"
              :editor="header.editor"
              :value="props.item[header.value]"
              :error-messages="getPathErrors(jsonPath, props.index, header.value)"
              :disabled="disabled"
              :set-cell-value="setCellValue"
              :build-ref-name="buildRefName"
              :jsonPath="buildJsonPath(props.index, header.value)"
              :jsonRowPath="buildJsonPath(props.index)"
              :clear-field-error-lookup="clearFieldErrorLookup"
              :self="_self"
            />
            <!-- else output text -->
            <template
              v-else
              v-show="!header.editor.hideIf || !header.editor.hideIf(props.item, props, header)"
            >
              {{ getCellText(header, props.item, i) }}
            </template>
          </td>
          <td
            v-if="!disableDelete || rowButtons"
            class="text-xs-right pr-0 ft-thin-cell"
          >
            <div class="ft-row-buttons">
              <slot
                name="row-buttons"
                :row="props.index"
                :item="props.item"
                :items="value"
                :disabled="disabled"
              />
              <v-btn
                v-if="!disableDelete"
                :disabled="!deleteItemCheck(props.item, props.index) || disabled"
                icon
                @click="deletePrompt(props.index, props.item, doDelete)"
              >
                <v-icon color="grey darken-1">
                  {{ deleteIcon }}
                </v-icon>
              </v-btn>
            </div>
          </td>
          <td
            v-if="hasWarnings"
            class="text-xs-right pr-0 ft-thin-cell"
          >
            <v-tooltip
              v-if="warnings[props.index]"
              top
            >
              <template v-slot:activator="{ on }">
                <v-icon
                  color="warning"
                >
                  mdi-alert
                </v-icon>
              </template>
              <div
                v-for="(warning, w) of warnings[props.index]"
                :key="w"
              >
                {{ warning }}
              </div>
            </v-tooltip>
          </td>
        </tr>
      </template>
    </v-data-table>
    <item-editor-buttons
      v-if="!hideActions || getPathErrors(jsonPath)._hasErrors || errorMessages"
      :disabled="disableAdd || disabled"
      :save-action="hideAdd || hideActions ? null : doAdd"
      :error-messages="errorMessages && getPathErrors(jsonPath) ? getPathErrors(jsonPath).concat(errorMessages) : getPathErrors(jsonPath) || errorMessages"
      :save-text="'Add ' + type"
    />
  </expansion-card>
</template>

<script>
import detailEditor from '../mixins/detailEditor'
import tableCommon from '../mixins/tableCommon'
import Sortable from 'sortablejs'
import {forceFloat, forceInt, forceCurrency} from '../util/helpers'
import ExpansionCard from './ExpansionCard'
import ItemEditorButtons from './ItemEditorButtons'

const textInputs = ['text', 'number', 'currency', 'int']

export default {
  name: 'DetailEditorTable',
  components: {ExpansionCard, ItemEditorButtons},
  mixins: [detailEditor, tableCommon],
  props: {
    icon: String,
    headers: Array,
    sortable: Boolean,
    required: Boolean,
    warnings: Array,
    errorMessages: Array,
    hideRows: {
      type: Object,
      default: () => {
        return {}
      }
    },
    rowButtons: Boolean,
    hideActions: Boolean,
    hideAdd: Boolean,
    customFilter: Function,
    setRowClass: Function,
    rowSelectAction: Function,
    search: String,
    deleteItemCheck: {
      type: Function,
      default: () => true
    }
  },

  data: function () {
    return {
      numberInputs: ['number', 'int', 'currency']
    }
  },
  computed: {
    columnHeaders () {
      return this.headers.concat(!this.disableDelete || this.rowButtons ? [{sortable: false}] : [])
    },
    hasWarnings () {
      return !!this.warnings && !!this.warnings.find(warning => !!warning)
    }
  },
  mounted () {
    console.log('dte', this)
    this.bindSortable()
  },
  methods: {
    forceFloat,
    forceInt,
    forceCurrency,
    rowSelected (item) {
      if (this.rowSelectAction) {
        this.rowSelectAction(item)
      }
    },
    getRowClass (item) {
      const rowClass = {'sortableRow': this.sortable}
      if (this.setRowClass) {
        this.setRowClass(item, rowClass)
      }
      return rowClass
    },
    blurredFormat (editor) {
      return (v) => {
        switch (editor.type) {

        case 'currency':
          return this.forceCurrency(v, editor.allowNegative)

        case 'number':
          return this.forceFloat(v, editor.allowNegative)

        case 'int':
          return this.forceInt(v, editor.allowNegative)

        }
        return v
      }
    },
    setFilteredResult (_self, item, field, editor) {
      switch (editor.type) {

      case 'currency':
        return _self.$set(item, field, _self.forceCurrency(item[field], editor.allowNegative))

      case 'number':
        return _self.$set(item, field, _self.forceFloat(item[field], editor.allowNegative))

      case 'int':
        return _self.$set(item, field, _self.forceInt(item[field], editor.allowNegative))

      }
      return
    },
    inputText (header, item, headerIndex) {
      if (header.editor && textInputs.includes(header.editor.type)) {
        return header.editor
      } else {
        return false
      }
    },
    inputSlot (header, item, headerIndex) {
      if (header.editor && header.editor.type === 'slot') {
        return header.editor
      } else {
        return false
      }
    },
    inputSwitch (header, item, headerIndex) {
      if (header.editor && header.editor.type === 'switch') {
        return header.editor
      } else {
        return false
      }
    },
    dragReorder ({oldIndex, newIndex}) {
      this.movePathErrors(this.jsonPath, oldIndex, newIndex)
      const movedItem = this.value.splice(oldIndex, 1)[0]
      this.value.splice(newIndex, 0, movedItem)
      // this.$emit('input', this.itemList)
    },
    dragStart (e) {
      this.$emit('dragstart', e)
    },
    bindSortable (attempts) {
      const editorListing = this.$refs.editorListing
      attempts = (attempts || 0) + 1
      if (!editorListing && attempts <= 10) {
        setTimeout(() => {
          this.bindSortable(attempts)
        }, (attempts - 1) * 2)
      } else if (editorListing) {
        /* eslint-disable no-new */
        new Sortable(editorListing.$el.getElementsByTagName('tbody')[0], {
          draggable: '.sortableRow',
          handle: '.sortableHandle',
          onEnd: this.dragReorder,
          onStart: this.dragStart,
          scrollSensitivity: 80
        })
      } else {
        console.warn('Couldn\'nt bind sortable to detail editor table after ' + attempts + 'tries')
      }
    }
  }
}
</script>
<style>
  .ft-detail-editor-table th, .ft-detail-editor-table td {
    padding: 0 8px !important;
  }
  .ft-detail-editor-table th {
    white-space: normal !important;
  }
  .ft-detail-editor-table td.ft-thin-cell {
    width: 0.1%;
    overflow: hidden;
  }
  /* Keep number inputs from shrinking too much */
  .ft-detail-editor-table td.ft-thin-cell input[type=number] {
    min-width: 30px;
  }
  .ft-detail-editor-table td.ft-small-cell {
    width: 18%;
    min-width: 150px;
  }
  td .ft-row-buttons {
    white-space: nowrap;
  }
  td .ft-row-buttons .btn + .btn{
    margin-left: 0;
  }
</style>
